import React, { FC, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { Loader } from '@modernloop/shared/helper-components';
import { ListSubheader, MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material';

import { useTaskQueuesSelectListQuery } from 'src/generated/mloop-graphql';

import { NO_QUEUE, PAGE_SIZE } from 'src/constants';

import { TaskQueueSelectZeroState } from './TaskQueueSelectZeroState';

type Props = {
  defaultTaskQueueId: string | null;
  selectedTaskQueueId: string | null;
  onOptionChange: (value: string) => void;
  groupQueue?: boolean;
};

const QueueSelect: FC<Props> = ({ selectedTaskQueueId, defaultTaskQueueId, onOptionChange, groupQueue }) => {
  const { t } = useTranslation();
  const [selectedQueueId, setSelectedQueueId] = useState('');
  const { data, loading } = useTaskQueuesSelectListQuery({
    variables: {
      input: {
        pageInput: {
          limit: PAGE_SIZE,
          offset: 0,
        },
      },
    },
  });

  const handleOptionSelect = (option: SelectChangeEvent) => {
    setSelectedQueueId(option.target.value);
    if (option.target.value === NO_QUEUE) {
      onOptionChange('');
      return;
    }
    onOptionChange(option.target.value);
  };

  const options = useMemo(() => {
    return (
      data?.taskQueueSearch?.items.map((queue) => {
        return {
          value: queue.id.toString(),
          label: t('hiring-dashboard-common:common.queueSelect.nameWithMembers', {
            name: queue.name,
            count: queue.memberCount,
          }),
          id: queue.id,
        };
      }) || []
    );
  }, [data?.taskQueueSearch?.items, t]);

  const selectedValue = selectedQueueId || selectedTaskQueueId || NO_QUEUE;
  const noQueueLabel = t('hiring-dashboard-filter:filter.noQueue');

  const renderContent = () => {
    if (loading) {
      return <Loader loading />;
    }

    if (options.length) {
      return options
        .filter((option) => {
          if (groupQueue) {
            return option.id !== defaultTaskQueueId;
          }
          return option;
        })
        .map((option) => (
          <MenuItem value={option.value} key={option.id}>
            <Stack maxWidth="280px" flexWrap="nowrap">
              <MaybeTooltip label={option.label} title={option.label} />
            </Stack>
          </MenuItem>
        ));
    }

    if (!options.length && !loading) {
      return <TaskQueueSelectZeroState />;
    }

    return null;
  };
  const taskQueueDefault = options.find((option) => option.id === defaultTaskQueueId);

  const selectJsx = () => {
    if (groupQueue) {
      return (
        <Select fullWidth onChange={handleOptionSelect} value={selectedValue}>
          <ListSubheader>{t('hiring-dashboard-common:common.select.jobDefault')}</ListSubheader>
          {!taskQueueDefault?.id && <MenuItem value={NO_QUEUE}>{noQueueLabel}</MenuItem>}
          {taskQueueDefault?.id && <MenuItem value={taskQueueDefault?.id}>{taskQueueDefault?.label}</MenuItem>}
          {!!options.length && <ListSubheader>{t('hiring-dashboard-filter:filter.otherQueues')}</ListSubheader>}
          {taskQueueDefault?.id && <MenuItem value={NO_QUEUE}>{noQueueLabel}</MenuItem>}
          {renderContent()}
        </Select>
      );
    }

    return (
      <Select fullWidth onChange={handleOptionSelect} value={selectedValue}>
        <MenuItem value={NO_QUEUE}>{noQueueLabel}</MenuItem>
        {renderContent()}
      </Select>
    );
  };

  return selectJsx();
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskQueuesSelectListQueryGQL = gql`
  query TaskQueuesSelectList($input: TaskQueueSearchInput!) {
    taskQueueSearch(input: $input) {
      items {
        id
        name
        memberCount
      }
    }
  }
`;

export default QueueSelect;
